import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Wire schemas for `/admin/promo-codes/*` — admin-arch §3.9, admin-prd §8.
 *
 * Promo code model is flexible per PRD §8.0:
 *   - Two independent percentages: `client_discount_percentage` (discount
 *     applied to the matching client payments) and `selector_payout_percentage`
 *     (payout accrued to the owner selector). Either may be 0 → covers
 *     the four PRD shapes (referral-only, discount-only, win-win, premium).
 *   - Scope is independent for each side: `first_only` or `all_payments`.
 *   - `owner_selector_id` is optional. When NULL, `selector_payout_percentage`
 *     MUST be 0 (no payout target).
 *   - `valid_from` / `valid_until` gate NEW activations only. Existing
 *     activations keep accruing per their stored row.
 *
 * Status is derived from `is_active`:
 *   - 'active'  — `is_active=true`. NEW activations allowed within the
 *     activation window.
 *   - 'blocked' — `is_active=false`. New activations refused. Existing
 *     activations keep accruing (PRD §8.6 acceptance).
 */
export const promoCodeStatusSchema = z.enum(['active', 'blocked']);
export const promoCodeScopeSchema = z.enum(['first_only', 'all_payments']);
/**
 * Code format regex. Mirrors the `promo_codes_code_format_check` DB
 * constraint — keeps validation in sync end-to-end. Uppercase ASCII +
 * digits + dash/underscore; 4..32 chars.
 */
export const PROMO_CODE_REGEX = /^[A-Z0-9_-]{4,32}$/;
const percentageSchema = z.number().int().min(0).max(100);
const usageLimitSchema = z.number().int().positive().max(1_000_000);
// ---------------------------------------------------------------------------
// Stats — shape attached to the card response (admin-prd §8.2 "Карточка
// промокода"). Computed on read; no separate column.
// ---------------------------------------------------------------------------
export const promoCodeStatsSchema = z.object({
    activation_count: z.number().int().nonnegative(),
    last_activation_at: isoDateTimeSchema.nullable(),
    total_client_discount_kop: z.number().int().nonnegative(),
    total_selector_payout_kop: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Base row shape — also used for the list item (PRD §8.2 table columns).
// ---------------------------------------------------------------------------
export const promoCodeSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    owner_selector_id: uuidSchema.nullable(),
    owner_selector_name: z.string().nullable(),
    client_discount_percentage: percentageSchema,
    client_discount_scope: promoCodeScopeSchema,
    selector_payout_percentage: percentageSchema,
    selector_payout_scope: promoCodeScopeSchema,
    is_active: z.boolean(),
    status: promoCodeStatusSchema,
    usage_limit: z.number().int().positive().nullable(),
    valid_from: isoDateTimeSchema.nullable(),
    valid_until: isoDateTimeSchema.nullable(),
    blocked_at: isoDateTimeSchema.nullable(),
    block_reason: z.string().nullable(),
    notes: z.string().nullable(),
    created_by: uuidSchema.nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
    activation_count: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// List filter (admin-prd §8.2). Admin sees all; selector sees only own codes
// — the selector scope is enforced server-side in the controller/service.
// ---------------------------------------------------------------------------
const Q_MAX = 200;
const CURSOR_MAX = 200;
export const promoCodeFilterSchema = z.object({
    q: z.string().min(1).max(Q_MAX).optional(),
    status: promoCodeStatusSchema.optional(),
    owner_user_id: uuidSchema.optional(),
    limit: z.coerce.number().int().positive().max(100).default(50),
    cursor: z.string().max(CURSOR_MAX).optional(),
});
// ---------------------------------------------------------------------------
// List response — keyset pagination, total intentionally null (mirrors
// audit-log / selectors keyset convention).
// ---------------------------------------------------------------------------
export const promoCodeListResponseSchema = z.object({
    items: z.array(promoCodeSchema),
    total: z.number().int().nonnegative().nullable(),
    next_cursor: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Card — base row + computed stats (admin-prd §8.2).
// ---------------------------------------------------------------------------
export const promoCodeCardSchema = promoCodeSchema.extend({
    stats: promoCodeStatsSchema,
});
// ---------------------------------------------------------------------------
// Create (admin-prd §8.2 creation form, PRD acceptance §8.6).
//
// Validation rules:
//   - `code` matches PROMO_CODE_REGEX (uppercase ASCII / digits / -_, 4..32).
//   - Percentages 0..100.
//   - `valid_from` / `valid_until` are REQUIRED per PRD §8.2 line 879
//     ("срок действия активации (с / по, обязательно ...)") — admins always
//     bound the activation window.
//   - Activation window <= 12 months (PRD §8.2 line 879 hard cap).
//   - `valid_until > valid_from`.
//   - If `owner_selector_id` is NULL then `selector_payout_percentage` MUST
//     be 0 (no payout target).
//   - At least one of the percentages must be > 0 (avoid no-op codes).
// ---------------------------------------------------------------------------
const MAX_VALIDITY_MONTHS = 12;
const MAX_VALIDITY_MS = MAX_VALIDITY_MONTHS * 31 * 24 * 60 * 60 * 1000;
export const promoCodeCreateSchema = z
    .object({
    code: z
        .string()
        .trim()
        .regex(PROMO_CODE_REGEX, 'Code must be 4..32 chars, uppercase letters / digits / -_ only'),
    owner_selector_id: uuidSchema.nullable().optional(),
    client_discount_percentage: percentageSchema.default(0),
    client_discount_scope: promoCodeScopeSchema.default('first_only'),
    selector_payout_percentage: percentageSchema.default(0),
    selector_payout_scope: promoCodeScopeSchema.default('first_only'),
    usage_limit: usageLimitSchema.nullable().optional(),
    // PRD §8.2 line 879 — activation window is mandatory; non-optional here so
    // the Zod gate refuses any POST that omits either side.
    valid_from: isoDateTimeSchema,
    valid_until: isoDateTimeSchema,
    notes: z.string().trim().max(2000).nullable().optional(),
})
    .strict()
    .superRefine((dto, ctx) => {
    if ((dto.owner_selector_id === null || dto.owner_selector_id === undefined) &&
        dto.selector_payout_percentage > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['selector_payout_percentage'],
            message: 'selector_payout_percentage must be 0 when owner_selector_id is not set',
        });
    }
    if (dto.client_discount_percentage === 0 &&
        dto.selector_payout_percentage === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['client_discount_percentage'],
            message: 'At least one of client_discount_percentage / selector_payout_percentage must be > 0',
        });
    }
    // Window checks run unconditionally — `valid_from` / `valid_until` are
    // required, so both are guaranteed to be present at this point.
    const from = new Date(dto.valid_from).getTime();
    const until = new Date(dto.valid_until).getTime();
    if (until <= from) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['valid_until'],
            message: 'valid_until must be after valid_from',
        });
    }
    else if (until - from > MAX_VALIDITY_MS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['valid_until'],
            message: `Activation window must not exceed ${MAX_VALIDITY_MONTHS} months`,
        });
    }
});
// ---------------------------------------------------------------------------
// Patch (admin-prd §8.2 / BUG-088).
//
// `created_by` is immutable. `code`, `owner_selector_id` and the two scope
// fields (`client_discount_scope`, `selector_payout_scope`) are editable
// ONLY while the promo has zero activations — once a promo is in flight
// the codes are shipped in user-facing links and the payout / scope rules
// are baked into existing subscription rows, so changing them retroactively
// would break the audit trail (PRD §8.6 acceptance — blocked promo does
// not block already-activated subscriptions; same invariant applies to
// mutating the live row). The service enforces the zero-activation guard
// and returns 422 PROMO_CODE_LOCKED when violated. The FE renders the
// fields as read-only with an explanatory tooltip when activations exist.
// ---------------------------------------------------------------------------
export const promoCodePatchSchema = z
    .object({
    code: z
        .string()
        .trim()
        .regex(PROMO_CODE_REGEX, 'Code must be 4..32 chars, uppercase letters / digits / -_ only')
        .optional(),
    owner_selector_id: uuidSchema.nullable().optional(),
    client_discount_percentage: percentageSchema.optional(),
    client_discount_scope: promoCodeScopeSchema.optional(),
    selector_payout_percentage: percentageSchema.optional(),
    selector_payout_scope: promoCodeScopeSchema.optional(),
    usage_limit: usageLimitSchema.nullable().optional(),
    valid_until: isoDateTimeSchema.nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
/**
 * Fields whose mutation is locked once the promo has at least one
 * activation. Mirrors the BE guard in `PromoCodesService.patch` so the FE
 * can render them read-only without an extra round-trip.
 */
export const PROMO_CODE_PATCH_LOCKED_AFTER_ACTIVATION = [
    'code',
    'owner_selector_id',
    'client_discount_scope',
    'selector_payout_scope',
];
// ---------------------------------------------------------------------------
// Block / unblock (admin-arch §3.9 + admin-prd §8.6).
// `reason` is required on block to make the audit trail meaningful.
// ---------------------------------------------------------------------------
export const promoCodeBlockSchema = z
    .object({
    reason: z.string().trim().min(1).max(500),
})
    .strict();
// ---------------------------------------------------------------------------
// Activations (admin-prd §8.2 — card activations table).
// Read-only surface. Status field is derived from the linked subscription
// status — for MVP we expose the raw subscription_status verbatim.
// ---------------------------------------------------------------------------
export const promoCodeActivationSchema = z.object({
    id: uuidSchema,
    promo_code_id: uuidSchema,
    account_id: uuidSchema.nullable(),
    account_name: z.string().nullable(),
    subscription_id: uuidSchema.nullable(),
    subscription_status: z.string().nullable(),
    activated_at: isoDateTimeSchema,
    discount_kop: z.number().int().nonnegative(),
    payout_kop_per_period: z.number().int().nonnegative(),
    total_client_discount_kop: z.number().int().nonnegative(),
    total_selector_payout_kop: z.number().int().nonnegative(),
});
export const promoCodeActivationFilterSchema = z.object({
    limit: z.coerce.number().int().positive().max(100).default(50),
    cursor: z.string().max(CURSOR_MAX).optional(),
});
export const promoCodeActivationListResponseSchema = z.object({
    items: z.array(promoCodeActivationSchema),
    total: z.number().int().nonnegative().nullable(),
    next_cursor: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Public validation response — `GET /public/promo/:code` (admin-arch §3.13,
// PRD §8.3). Wave 2.39 BE-1 (TASK-101).
//
// Anti-enumeration shape: success surfaces ONLY the fields a signup form
// needs to render the discount preview. We deliberately omit:
//   - `selector_payout_percentage` / `selector_payout_scope` — internal
//     economics that must never reach the public.
//   - `owner_selector_id` / `owner_selector_name` — owner identity.
//   - `usage_limit`, `activation_count`, `valid_from`, `notes`,
//     `created_by`, timestamps — operational metadata, no public utility.
//   - `is_active`, `blocked_at`, `block_reason` — a blocked code returns
//     404, never a "valid:false" body, so these never need to ship.
//
// `valid_until` IS exposed — the wizard wants to render "скидка действует
// до DD.MM.YYYY" without a second round-trip.
//
// `code` echoes back the NORMALISED (uppercase) form. The path segment is
// normalised before lookup so callers using a lowercase URL still get the
// canonical token back.
// ---------------------------------------------------------------------------
export const publicPromoValidationResponseSchema = z.object({
    valid: z.literal(true),
    code: z.string(),
    client_discount_percentage: percentageSchema,
    client_discount_scope: promoCodeScopeSchema,
    valid_until: isoDateTimeSchema.nullable(),
});
//# sourceMappingURL=promo-code.js.map